import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import AlertComponent from "../../components/AlertComponent";
import { buildGuestConfirmationUrl } from "../../utils/publicEventUrl";

let manualTempId = 1;

function criarConvidadoManual(overrides = {}) {
  return {
    tempId: manualTempId++,
    id: null,
    nome: "",
    nomeTratamento: "",
    email: "",
    telefone: "",
    crianca: false,
    idade: "",
    ...overrides,
  };
}

export default function ConvidadosEvento({ eventId, embedded = false } = {}) {
  const { id: routeId } = useParams();
  const id = eventId ?? routeId;
  const navigate = useNavigate();
  const [evento, setEvento] = useState(null);
  const [convidados, setConvidados] = useState([]);
  const [arquivo, setArquivo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [manualForm, setManualForm] = useState(null);
  const [salvandoManual, setSalvandoManual] = useState(false);
  const [excluindoManual, setExcluindoManual] = useState(false);

  useEffect(() => {
    if (!id) return;

    async function carregar() {
      setLoading(true);
      try {
        const [eventoRes, convidadosRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API}/evento/${id}`),
          axios.get(`${process.env.REACT_APP_API}/convidado/evento/${id}`),
        ]);
        setEvento(eventoRes.data);
        setConvidados(convidadosRes.data || []);
      } catch (err) {
        console.error(err);
        AlertComponent("Erro", "Erro ao carregar convidados", { icon: "error" });
      } finally {
        setLoading(false);
      }
    }

    carregar();
  }, [id]);

  const stats = useMemo(() => {
    const total = convidados.length;
    const grupos = new Set(convidados.map((c) => c.codigoConfirmacao).filter(Boolean)).size;
    const confirmados = convidados.filter((c) => c.statusConfirmacao === 1).length;
    const recusados = convidados.filter((c) => c.statusConfirmacao === 2).length;
    const pendentes = convidados.filter((c) => c.statusConfirmacao == null || c.statusConfirmacao === 0).length;
    const enviados = convidados.filter((c) => c.mensagemEnviada).length;
    return { total, grupos, confirmados, recusados, pendentes, enviados };
  }, [convidados]);

  async function importar(e) {
    e.preventDefault();
    if (!arquivo) {
      AlertComponent("Arquivo obrigatório", "Selecione uma planilha .xlsx ou .csv", { icon: "warning" });
      return;
    }

    setImportando(true);
    setResultado(null);
    try {
      const formData = new FormData();
      formData.append("arquivo", arquivo);
      const res = await axios.post(`${process.env.REACT_APP_API}/convidado/evento/${id}/importar`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setConvidados(res.data.convidados || []);
      setResultado(res.data);
      setArquivo(null);
      e.target.reset();
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", err.response?.data?.mensagem || "Erro ao importar convidados", { icon: "error" });
    } finally {
      setImportando(false);
    }
  }

  async function copiarLink(convidado) {
    const link = buildGuestConfirmationUrl(evento, convidado.codigoConfirmacao);
    await navigator.clipboard.writeText(link);
    AlertComponent("Link copiado", link, { icon: "success", timer: 1800 });
  }

  function abrirAvulso() {
    setResultado(null);
    setManualForm({
      modo: "avulso",
      codigoConfirmacao: null,
      grupoConvite: "",
      convidados: [criarConvidadoManual()],
    });
  }

  function abrirFamilia() {
    setResultado(null);
    setManualForm({
      modo: "familia",
      codigoConfirmacao: null,
      grupoConvite: "",
      convidados: [criarConvidadoManual()],
    });
  }

  function editarGrupo(convidado) {
    const grupo = convidados
      .filter((item) => item.codigoConfirmacao === convidado.codigoConfirmacao)
      .sort((a, b) => a.nome.localeCompare(b.nome));

    setResultado(null);
    setManualForm({
      modo: grupo.length > 1 || convidado.grupoConvite ? "familia" : "avulso",
      codigoConfirmacao: convidado.codigoConfirmacao,
      grupoConvite: convidado.grupoConvite || "",
      convidados: grupo.map((item) =>
        criarConvidadoManual({
          id: item.id,
          nome: item.nome || "",
          nomeTratamento: item.nomeTratamento || "",
          email: item.email || "",
          telefone: item.telefone || "",
          crianca: item.crianca || false,
          idade: item.crianca ? String(item.idade ?? "") : "",
        })
      ),
    });
  }

  function atualizarConvidadoManual(tempId, campo, valor) {
    setManualForm((current) => ({
      ...current,
      convidados: current.convidados.map((convidado) =>
        convidado.tempId === tempId ? { ...convidado, [campo]: valor } : convidado
      ),
    }));
  }

  function adicionarPessoaManual() {
    setManualForm((current) => ({
      ...current,
      convidados: [...current.convidados, criarConvidadoManual()],
    }));
  }

  function removerPessoaManual(tempId) {
    setManualForm((current) => ({
      ...current,
      convidados: current.convidados.length <= 1
        ? current.convidados
        : current.convidados.filter((convidado) => convidado.tempId !== tempId),
    }));
  }

  async function salvarManual(e) {
    e.preventDefault();
    if (!manualForm) return;

    if (manualForm.convidados.some((convidado) => !convidado.nome.trim())) {
      AlertComponent("Nome obrigatório", "Informe o nome de todos os convidados", { icon: "warning" });
      return;
    }

    if (manualForm.modo === "familia" && !manualForm.grupoConvite.trim()) {
      AlertComponent("Família obrigatória", "Informe o nome da família ou grupo", { icon: "warning" });
      return;
    }

    if (manualForm.convidados.some((convidado) => convidado.crianca && idadeCriancaInvalida(convidado.idade))) {
      AlertComponent("Idade inválida", "Informe idade entre 0 e 17 anos para as crianças", { icon: "warning" });
      return;
    }

    const convidadosPayload = manualForm.convidados.map((convidado) => ({
      id: convidado.id,
      nome: convidado.nome.trim(),
      nomeTratamento: convidado.nomeTratamento.trim() || null,
      email: convidado.email.trim() || null,
      telefone: convidado.telefone.trim() || null,
      crianca: convidado.crianca,
      idade: convidado.crianca ? Number(convidado.idade) : null,
    }));

    setSalvandoManual(true);
    try {
      const payload = {
        grupoConvite: manualForm.modo === "familia" ? manualForm.grupoConvite.trim() : null,
        convidados: convidadosPayload,
      };

      const endpoint = manualForm.codigoConfirmacao
        ? `${process.env.REACT_APP_API}/convidado/evento/${id}/manual/${manualForm.codigoConfirmacao}`
        : `${process.env.REACT_APP_API}/convidado/evento/${id}/manual`;

      const res = manualForm.codigoConfirmacao
        ? await axios.put(endpoint, payload)
        : await axios.post(endpoint, payload);

      setConvidados(res.data.convidados || []);
      setManualForm(null);
      AlertComponent("Sucesso", "Convidados salvos com sucesso", { icon: "success", timer: 1800 });
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", err.response?.data?.mensagem || "Erro ao salvar convidados", { icon: "error" });
    } finally {
      setSalvandoManual(false);
    }
  }

  async function excluirManual() {
    if (!manualForm?.codigoConfirmacao) return;

    const alvo = manualForm.modo === "familia" ? "esta família" : "este convidado";
    if (!window.confirm(`Excluir ${alvo}?`)) return;

    setExcluindoManual(true);
    try {
      const res = await axios.delete(`${process.env.REACT_APP_API}/convidado/evento/${id}/manual/${manualForm.codigoConfirmacao}`);
      setConvidados(res.data.convidados || []);
      setManualForm(null);
      AlertComponent("Sucesso", "Convidados excluídos", { icon: "success", timer: 1800 });
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", err.response?.data?.mensagem || "Erro ao excluir convidados", { icon: "error" });
    } finally {
      setExcluindoManual(false);
    }
  }

  async function baixarModelo() {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API}/convidado/modelo-importacao`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = obterNomeArquivo(res.headers["content-disposition"]) || "modelo-convidados.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", "Erro ao baixar modelo de convidados", { icon: "error" });
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className={embedded ? "w-full" : "max-w-6xl mx-auto"}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          {!embedded && (
            <button onClick={() => navigate("/eventos")} className="text-sm font-semibold text-slate-500 hover:text-violet-700 mb-2">
              Voltar
            </button>
          )}
          <h1 className="text-2xl font-bold text-slate-800">Convidados</h1>
          <p className="text-sm text-slate-500 mt-0.5">{evento?.nome}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={abrirAvulso} className="px-4 py-2 text-sm font-semibold rounded-xl bg-violet-600 text-white hover:bg-violet-700">
            Adicionar avulso
          </button>
          <button onClick={abrirFamilia} className="px-4 py-2 text-sm font-semibold rounded-xl bg-slate-900 text-white hover:bg-slate-800">
            Adicionar família
          </button>
          <button onClick={baixarModelo} className="px-4 py-2 text-sm font-semibold rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200">
            Baixar modelo XLSX
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-5">
        <Stat label="Convidados" value={stats.total} />
        <Stat label="Grupos" value={stats.grupos} />
        <Stat label="Confirmados" value={stats.confirmados} tone="emerald" />
        <Stat label="Recusaram" value={stats.recusados} tone="rose" />
        <Stat label="Pendentes" value={stats.pendentes} tone="amber" />
        <Stat label="Mensagem enviada" value={stats.enviados} tone="violet" />
      </div>

      {manualForm && (
        <ManualForm
          form={manualForm}
          setForm={setManualForm}
          salvando={salvandoManual}
          excluindo={excluindoManual}
          onSalvar={salvarManual}
          onCancelar={() => setManualForm(null)}
          onExcluir={excluirManual}
          onAdicionarPessoa={adicionarPessoaManual}
          onRemoverPessoa={removerPessoaManual}
          onAtualizarPessoa={atualizarConvidadoManual}
        />
      )}

      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 p-5 mb-5">
        <form onSubmit={importar} className="flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1">
            <label className="block text-sm font-semibold text-slate-700 mb-1">Importar lista</label>
            <input
              type="file"
              accept=".xlsx,.csv,.txt"
              onChange={(e) => setArquivo(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-600 file:mr-4 file:rounded-lg file:border-0 file:bg-violet-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-violet-700 hover:file:bg-violet-100"
            />
            <p className="text-xs text-slate-400 mt-1">
              Baixe o modelo XLSX para preencher no padrão certo. E-mail é opcional. Marque crianças e informe a idade para calcular pagantes.
            </p>
          </div>
          <button
            type="submit"
            disabled={importando}
            className="px-5 py-2.5 text-sm font-semibold rounded-xl text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-60"
          >
            {importando ? "Importando..." : "Importar"}
          </button>
        </form>

        {resultado && (
          <div className="mt-4 rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
            {resultado.criados} criado(s), {resultado.atualizados} atualizado(s), {resultado.erros?.length || 0} erro(s).
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 overflow-hidden">
        {convidados.length === 0 ? (
          <div className="text-center py-16 px-6">
            <p className="text-slate-600 font-semibold">Nenhum convidado cadastrado</p>
            <p className="text-slate-400 text-sm mt-1">Adicione avulsos, cadastre uma família ou suba uma lista para gerar os códigos.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1120px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Nome</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Contato</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Grupo</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Código</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Link</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {convidados.map((convidado) => (
                  <tr key={convidado.id} className="hover:bg-slate-50">
                    <td className="px-5 py-4">
                      <p className="font-semibold text-slate-800">{convidado.nome}</p>
                      {convidado.nomeTratamento && (
                        <p className="text-xs text-slate-400 mt-0.5">{convidado.nomeTratamento}</p>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-500">
                      <p>{convidado.email || "-"}</p>
                      <p>{convidado.telefone || "-"}</p>
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-500">
                      {obterTipoConvidado(convidado)}
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-500">
                      {convidado.grupoConvite || "-"}
                    </td>
                    <td className="px-4 py-4">
                      <code className="rounded-lg bg-slate-100 px-2 py-1 text-sm font-semibold text-slate-700">{convidado.codigoConfirmacao}</code>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${obterStatusConvidado(convidado).className}`}>
                        {obterStatusConvidado(convidado).label}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <button onClick={() => copiarLink(convidado)} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100">
                        Copiar
                      </button>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <button onClick={() => editarGrupo(convidado)} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200">
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ManualForm({
  form,
  setForm,
  salvando,
  excluindo,
  onSalvar,
  onCancelar,
  onExcluir,
  onAdicionarPessoa,
  onRemoverPessoa,
  onAtualizarPessoa,
}) {
  const editando = !!form.codigoConfirmacao;
  const familia = form.modo === "familia";

  return (
    <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/60 p-5 mb-5">
      <form onSubmit={onSalvar} className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <p className="text-sm font-bold text-slate-800">
              {editando ? "Editar" : "Adicionar"} {familia ? "família" : "convidado avulso"}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {familia
                ? "Todos os nomes desta família compartilharão o mesmo código de confirmação."
                : "Convidado avulso recebe um código individual."}
            </p>
          </div>
          {editando && (
            <code className="self-start rounded-lg bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
              {form.codigoConfirmacao}
            </code>
          )}
        </div>

        {familia && (
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Família/grupo</label>
            <input
              type="text"
              value={form.grupoConvite}
              onChange={(e) => setForm((current) => ({ ...current, grupoConvite: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
              placeholder="Ex: Família Fabio"
              disabled={salvando}
            />
          </div>
        )}

        <div className="space-y-3">
          {form.convidados.map((convidado, index) => (
            <div key={convidado.tempId} className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr_1fr_0.9fr_0.75fr_0.65fr_auto] gap-3 rounded-xl bg-slate-50 p-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Nome</label>
                <input
                  type="text"
                  value={convidado.nome}
                  onChange={(e) => onAtualizarPessoa(convidado.tempId, "nome", e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                  placeholder={familia ? `Pessoa ${index + 1}` : "Nome do convidado"}
                  disabled={salvando}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Tratamento</label>
                <input
                  type="text"
                  value={convidado.nomeTratamento}
                  onChange={(e) => onAtualizarPessoa(convidado.tempId, "nomeTratamento", e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                  placeholder="Opcional"
                  disabled={salvando}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">E-mail</label>
                <input
                  type="email"
                  value={convidado.email}
                  onChange={(e) => onAtualizarPessoa(convidado.tempId, "email", e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                  placeholder="Opcional"
                  disabled={salvando}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Telefone</label>
                <input
                  type="tel"
                  value={convidado.telefone}
                  onChange={(e) => onAtualizarPessoa(convidado.tempId, "telefone", e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                  placeholder="Opcional"
                  disabled={salvando}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Tipo</label>
                <select
                  value={convidado.crianca ? "crianca" : "adulto"}
                  onChange={(e) => {
                    const crianca = e.target.value === "crianca";
                    onAtualizarPessoa(convidado.tempId, "crianca", crianca);
                    if (!crianca) onAtualizarPessoa(convidado.tempId, "idade", "");
                  }}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                  disabled={salvando}
                >
                  <option value="adulto">Adulto</option>
                  <option value="crianca">Criança</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1">Idade</label>
                <input
                  type="number"
                  min="0"
                  max="17"
                  step="1"
                  value={convidado.idade}
                  onChange={(e) => onAtualizarPessoa(convidado.tempId, "idade", e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-400"
                  placeholder={convidado.crianca ? "Obrigatória" : "-"}
                  disabled={salvando || !convidado.crianca}
                  required={convidado.crianca}
                />
              </div>
              <div className="flex items-end">
                {familia && form.convidados.length > 1 && (
                  <button
                    type="button"
                    onClick={() => onRemoverPessoa(convidado.tempId)}
                    className="w-full lg:w-auto rounded-lg border border-red-100 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
                    disabled={salvando}
                  >
                    Remover
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {familia && (
          <button
            type="button"
            onClick={onAdicionarPessoa}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            disabled={salvando}
          >
            + Adicionar pessoa
          </button>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-slate-100 pt-4">
          <div>
            {editando && (
              <button
                type="button"
                onClick={onExcluir}
                disabled={salvando || excluindo}
                className="rounded-xl border border-red-100 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60"
              >
                {excluindo ? "Excluindo..." : familia ? "Excluir família" : "Excluir convidado"}
              </button>
            )}
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={onCancelar}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              disabled={salvando || excluindo}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="rounded-xl bg-slate-900 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
              disabled={salvando || excluindo}
            >
              {salvando ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Stat({ label, value, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
    amber: "bg-amber-50 text-amber-700",
    violet: "bg-violet-50 text-violet-700",
  };

  return (
    <div className={`rounded-2xl p-4 ${tones[tone]}`}>
      <p className="text-2xl font-extrabold">{value}</p>
      <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</p>
    </div>
  );
}

function obterStatusConvidado(convidado) {
  if (convidado.statusConfirmacao === 1) {
    return { label: "Confirmado", className: "bg-emerald-100 text-emerald-700" };
  }

  if (convidado.statusConfirmacao === 2) {
    return { label: "Recusou", className: "bg-rose-100 text-rose-700" };
  }

  if (convidado.statusConfirmacao === 3) {
    return { label: "Talvez", className: "bg-sky-100 text-sky-700" };
  }

  return { label: "Pendente", className: "bg-amber-100 text-amber-700" };
}

function obterTipoConvidado(convidado) {
  if (!convidado.crianca) return "Adulto";

  const idade = convidado.idade ?? 0;
  return `Criança (${idade} ano${idade === 1 ? "" : "s"})`;
}

function idadeCriancaInvalida(value) {
  const texto = String(value ?? "").trim();
  const idade = Number(texto);

  return texto === "" || !Number.isInteger(idade) || idade < 0 || idade > 17;
}

function obterNomeArquivo(contentDisposition) {
  if (!contentDisposition) return null;

  const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(contentDisposition);
  return match ? decodeURIComponent(match[1]) : null;
}
